import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

// Pipeline seller detail card (Kelly, Nov 2, 2026): labeled fact grid, pool/view chips, a collapsible Notes
// section, ONE "CMAs & Net Sheets" section (pipeline docs + saved CMA drafts, one empty state), and one primary
// action row with Edit / Open lead profile as smaller secondary actions. Works from 320px phones to desktop.
const GOLD = '#D4AF37'; const INK = '#1a1a1a'; const MUTED = '#666666';
const css = `
.sdc-grid{display:grid;grid-template-columns:1fr;max-width:640px;margin:12px 0 4px}
.sdc-row{display:grid;grid-template-columns:118px 1fr;gap:8px;padding:7px 0;border-bottom:1px solid #f0ead8;font-size:14px;min-width:0}
.sdc-row dt{color:${MUTED};font-weight:600;margin:0}.sdc-row dd{margin:0;overflow-wrap:anywhere;color:${INK}}
.sdc-primary{display:flex;flex-wrap:wrap;gap:8px;margin-top:14px;max-width:640px}
@media (max-width:520px){.sdc-row{grid-template-columns:104px 1fr}}`;
const chip = { display: 'inline-block', fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 12, border: `1px solid ${GOLD}`, background: '#FBF7EC', color: INK, marginRight: 6, marginTop: 4 };
const section = { marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e5e5' };
const h3 = { fontWeight: 700, fontSize: 14, margin: '0 0 6px', color: INK };
const primary = { minHeight: 44, padding: '10px 16px', borderRadius: 8, border: `1px solid ${GOLD}`, background: GOLD, color: INK, fontWeight: 700, cursor: 'pointer', fontSize: 14, flex: '1 1 150px' };
const secondary = { background: 'none', border: 'none', color: '#8C6D1F', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: '6px 4px', textDecoration: 'underline' };
const NOTE_LINES = 3;

export const priceText = (r, priceRange) => priceRange(r.priceMin, r.priceMax) || (r.estimatedPrice ? `Est. ${r.estimatedPrice}` : '');
// The labeled facts, in Kelly's order. Empty values show an em dash so the grid stays aligned.
export const sellerFacts = (r, { agent, status, priceRange }) => [
  ['Status', status], ['Timeline', r.timeline], ['Price range', priceText(r, priceRange)], ['Community', [r.community, r.floorPlan].filter(Boolean).join(' · ')],
  ['Address', r.propertyAddress], ['Phone', r.phone], ['Email', r.email], ['Agent', agent], ['Also buying', r.alsoBuying],
].filter(([k, v]) => k !== 'Email' || v).map(([k, v]) => [k, v || '—']);

export default function SellerDetailCard({ r, header, statusText, agent, priceRange, docs, savedCmas, apptText, actions, extra }) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(0);
  const onCount = useCallback((n) => setSaved(n), []);
  const notes = String(r.notes || '').trim();
  const long = notes.split('\n').length > NOTE_LINES || notes.length > 220;
  return (
    <div>
      <style>{css}</style>
      {header}
      {(r.pool || r.view) && <div aria-label="Features">{r.pool && <span style={chip}>Pool: {r.pool}</span>}{r.view && <span style={chip}>View: {r.view}</span>}</div>}
      <dl className="sdc-grid">{sellerFacts(r, { agent, status: statusText, priceRange }).map(([k, v]) => <div className="sdc-row" key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
      {apptText && <div style={{ fontSize: 14, marginTop: 6 }}><strong>Listing appointment:</strong> {apptText}</div>}
      {extra}
      <div className="sdc-primary">
        <button style={primary} onClick={actions.cma}>Create CMA</button>
        <button style={primary} onClick={actions.netSheet}>Create Net Sheet</button>
        {actions.logAppt && <button style={primary} onClick={actions.logAppt}>Log appointment</button>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 6 }}>
        <button style={secondary} onClick={actions.edit}>Edit</button>
        {actions.leadProfile && <Link to={actions.leadProfile} style={{ ...secondary, display: 'inline-block' }}>Open lead profile</Link>}
        {actions.more}
      </div>
      <div style={section}>
        <div style={h3}>Notes</div>
        {notes ? <>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...(long && !open ? { display: '-webkit-box', WebkitLineClamp: NOTE_LINES, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}) }}>{notes}</div>
          {long && <button style={{ ...secondary, paddingLeft: 0 }} aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Show less' : 'Show more'}</button>}
        </> : <div style={{ fontSize: 13, color: MUTED }}>No notes yet.</div>}
      </div>
      <div style={section}>
        <div style={h3}>CMAs & Net Sheets</div>
        {docs.length === 0 && saved === 0 && <div style={{ fontSize: 13, color: MUTED }}>None yet.</div>}
        {docs.map((d) => <div key={d.k} style={{ fontSize: 14, padding: '4px 0' }}>{d.kind}: {d.title || d.fileName || 'Untitled'}{d.at ? ` · ${new Date(d.at).toLocaleDateString()}` : ''}{d.example ? ' (example)' : ''} · <Link to={d.to}>Open</Link></div>)}
        {savedCmas && savedCmas(onCount)}
      </div>
    </div>
  );
}
